import { useState, useEffect } from 'react';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { 
  CreditCard, 
  Receipt, 
  TrendingUp, 
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Euro,
  FileText,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

const statusConfig = {
  pending: { label: 'Openstaand', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  paid: { label: 'Betaald', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  disputed: { label: 'Betwist', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  waived: { label: 'Kwijtgescholden', color: 'bg-neutral-100 text-neutral-600 border-neutral-200', icon: XCircle },
  invoiced: { label: 'Gefactureerd', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: FileText }
};

const StatCard = ({ icon: Icon, label, value, subValue, iconBgColor }) => (
  <Card className="border-neutral-200">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-neutral-500">{label}</p>
          <p className="text-2xl font-bold text-neutral-900 mt-1">{value}</p>
          {subValue && <p className="text-xs text-neutral-400 mt-1">{subValue}</p>}
        </div>
        <div className={`h-12 w-12 ${iconBgColor} rounded-xl flex items-center justify-center`}>
          <Icon className="h-6 w-6 text-neutral-700" strokeWidth={1.5} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SupplierFinancePage = () => {
  const { api } = useSupplierAuth();
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ledger');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [page, statusFilter, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'ledger') {
        const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
        if (statusFilter !== 'all') params.append('status', statusFilter);
        
        const res = await api.get(`/finance/ledger?${params}`);
        setLedger(res.data.entries || []);
        setSummary(res.data.summary || {});
        setTotalItems(res.data.total || 0);
      } else {
        const res = await api.get(`/finance/invoices?page=${page}&limit=${limit}`);
        setInvoices(res.data.invoices || []);
        setTotalItems(res.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals from summary
  const totalPending = summary.pending?.total || 0;
  const totalPaid = summary.paid?.total || 0;
  const totalDisputed = summary.disputed?.total || 0;
  const countPending = summary.pending?.count || 0;
  const countPaid = summary.paid?.count || 0;

  const totalPages = Math.ceil(totalItems / limit);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'd MMM yyyy', { locale: nl });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Financiën</h1>
        <p className="text-neutral-500 mt-1">Beheer uw lead fees en facturen</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={Clock}
          label="Openstaand"
          value={formatCurrency(totalPending)}
          subValue={`${countPending} lead${countPending !== 1 ? 's' : ''}`}
          iconBgColor="bg-amber-50"
        />
        <StatCard
          icon={CheckCircle}
          label="Betaald"
          value={formatCurrency(totalPaid)}
          subValue={`${countPaid} lead${countPaid !== 1 ? 's' : ''}`}
          iconBgColor="bg-green-50"
        />
        <StatCard
          icon={AlertTriangle}
          label="Betwist"
          value={formatCurrency(totalDisputed)}
          subValue="In behandeling"
          iconBgColor="bg-red-50"
        />
        <StatCard
          icon={Euro}
          label="Totaal (YTD)"
          value={formatCurrency(totalPending + totalPaid)}
          subValue={`${countPending + countPaid} leads`}
          iconBgColor="bg-blue-50"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200">
        <button
          onClick={() => { setActiveTab('ledger'); setPage(1); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'ledger' 
              ? 'border-black text-black' 
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
          data-testid="finance-tab-ledger"
        >
          <Receipt className="h-4 w-4 inline-block mr-2" />
          Lead Ledger
        </button>
        <button
          onClick={() => { setActiveTab('invoices'); setPage(1); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'invoices' 
              ? 'border-black text-black' 
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
          data-testid="finance-tab-invoices"
        >
          <FileText className="h-4 w-4 inline-block mr-2" />
          Facturen
        </button>
      </div>

      {/* Ledger Tab */}
      {activeTab === 'ledger' && (
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Lead Fee Overzicht</CardTitle>
                <CardDescription>Alle lead fees en hun status</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-neutral-400" />
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]" data-testid="ledger-status-filter">
                    <SelectValue placeholder="Filter status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle statussen</SelectItem>
                    <SelectItem value="pending">Openstaand</SelectItem>
                    <SelectItem value="paid">Betaald</SelectItem>
                    <SelectItem value="disputed">Betwist</SelectItem>
                    <SelectItem value="waived">Kwijtgescholden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
              </div>
            ) : ledger.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                <p className="text-neutral-500">Geen lead fees gevonden</p>
                <p className="text-sm text-neutral-400 mt-1">
                  Lead fees worden gegenereerd wanneer u leads accepteert
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead>Lead ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Bedrag</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Betwist tot</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.map((entry) => {
                      const status = statusConfig[entry.status] || statusConfig.pending;
                      const StatusIcon = status.icon;
                      return (
                        <TableRow key={entry.id} data-testid={`ledger-row-${entry.id}`}>
                          <TableCell className="text-neutral-600">
                            {formatDate(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-neutral-100 px-2 py-1 rounded">
                              {entry.lead_id?.slice(0, 8)}...
                            </code>
                          </TableCell>
                          <TableCell className="capitalize">
                            {entry.type === 'lead_fee' ? 'Lead Fee' : entry.type}
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(entry.amount)}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${status.color} border`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-neutral-500 text-sm">
                            {entry.dispute_window_ends ? formatDate(entry.dispute_window_ends) : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-100">
                    <p className="text-sm text-neutral-500">
                      Pagina {page} van {totalPages} ({totalItems} items)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <Card className="border-neutral-200">
          <CardHeader>
            <CardTitle className="text-lg">Facturen</CardTitle>
            <CardDescription>Maandelijkse facturen voor lead fees</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                <p className="text-neutral-500">Nog geen facturen</p>
                <p className="text-sm text-neutral-400 mt-1">
                  Facturen worden maandelijks gegenereerd voor openstaande lead fees
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Factuurnummer</TableHead>
                      <TableHead>Periode</TableHead>
                      <TableHead>Bedrag</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Vervaldatum</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <TableCell className="font-medium">
                          {invoice.invoice_number || `INV-${invoice.id?.slice(0, 6)}`}
                        </TableCell>
                        <TableCell className="text-neutral-600">
                          {invoice.period || formatDate(invoice.created_at)}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(invoice.total_amount || invoice.amount || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            invoice.status === 'paid' 
                              ? 'bg-green-100 text-green-700 border-green-200' 
                              : 'bg-amber-100 text-amber-700 border-amber-200'
                          }>
                            {invoice.status === 'paid' ? 'Betaald' : 'Openstaand'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-neutral-500">
                          {formatDate(invoice.due_date)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" disabled>
                            <FileText className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-100">
                    <p className="text-sm text-neutral-500">
                      Pagina {page} van {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-neutral-200 bg-neutral-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <CreditCard className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">Over Lead Fees</p>
              <p className="text-sm text-neutral-600 mt-1">
                U betaalt een lead fee van €50 voor elke geaccepteerde lead. U heeft 14 dagen om een lead te betwisten 
                indien deze niet voldoet aan de kwaliteitseisen. Openstaande fees worden maandelijks gefactureerd.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplierFinancePage;
